import React, { useContext, useEffect, useRef } from "react";

import { isClient } from "@kikoff/utils/src/general";
import { clamp } from "@kikoff/utils/src/number";

import useUpdate from "./useUpdate";

export default function useContentHeight<T extends HTMLElement = any>({
  min = 0,
  max = isClient && window.innerHeight,
  disabled = false,
}) {
  const baseRef = useRef<T>(null);
  const height = useRef(min);
  const recencySet = useRef(new Set<Node>()).current;
  const adjustmentRootMap = new Map<Node, number>();

  const update = useUpdate.single();
  const refresh = useUpdate();

  return {
    ref: baseRef,
    refresh,
    height: height.current,
    disabled,
    makeAdjustment(element: Element, adjustment: number) {
      recencySet.add(element);

      const root = (() => {
        for (const node of adjustmentRootMap.keys()) {
          if (node.contains(element)) return node;
        }
        return null;
      })();

      if (root)
        adjustmentRootMap.set(root, adjustmentRootMap.get(root) + adjustment);
      else adjustmentRootMap.set(element, adjustment);

      // Get height from most recent ref
      const newHeight = (() => {
        let finalAdjustment = baseRef.current.clientHeight;
        for (const node of recencySet) {
          if (adjustmentRootMap.has(node))
            finalAdjustment = adjustmentRootMap.get(node);
        }

        return clamp(baseRef.current.clientHeight + finalAdjustment, {
          min,
          max,
        });
      })();

      const diff = newHeight - height.current;
      if (diff < 0 && diff > -5) return;
      height.current = newHeight;
      update();
    },
    unmount(element: Element) {
      recencySet.delete(element);
    },
  };
}

type ContentHeight = ReturnType<typeof useContentHeight>;

declare namespace useMaximizeScrollContainer {
  export interface Options {
    contentRef?: React.MutableRefObject<HTMLElement>;
    elementHeightMap?: Map<Element, number>;
  }
}
function useMaximizeScrollContainer<T extends HTMLElement = any>({
  contentRef,
  elementHeightMap,
}: useMaximizeScrollContainer.Options = {}) {
  const ref = useRef<T>(null);

  const {
    makeAdjustment,
    refresh,
    unmount,
    disabled,
  } = useContentHeightContext();

  useEffect(() => {
    if (disabled) return;

    const { current } = ref;
    return () => {
      unmount(current);
    };
  }, []);

  useEffect(() => {
    if (disabled) return;
    if (!ref.current) return;

    const contentHeight = [
      ...(contentRef ? contentRef.current : ref.current).children,
    ].reduce(
      (acc, element) =>
        acc + (elementHeightMap?.get(element) || element.scrollHeight),
      0
    );

    const diff = contentHeight - ref.current.clientHeight;
    makeAdjustment(ref.current, diff);
  }, [refresh]);

  return ref;
}

export { useMaximizeScrollContainer };

export const ContentHeightContext = React.createContext<ContentHeight>({
  ref: { current: null },
  height: 0,
  refresh() {},
  makeAdjustment() {},
  unmount() {},
  disabled: true,
});

interface ContentHeightProviderProps {
  children:
    | ((contentHeight: ContentHeight) => React.ReactNode)
    | React.ReactNode;
  contentHeight: ContentHeight;
}

export function ContentHeightProvider({
  children,
  contentHeight,
}: ContentHeightProviderProps) {
  return (
    <ContentHeightContext.Provider value={contentHeight}>
      {typeof children === "function" ? children(contentHeight) : children}
    </ContentHeightContext.Provider>
  );
}
export const useContentHeightContext = () => useContext(ContentHeightContext);
